/**
 * Route → breadcrumb trail used by the top header bar to render "Section › … › Page"
 * (next to notifications / user menu), instead of each page rendering its own breadcrumb row.
 * Keeps labels + parent sections consistent with the sidebar nav (`components/layout/Sidebar.tsx`).
 *
 * Rules are matched top-to-bottom, so list MORE SPECIFIC / nested routes BEFORE their parents
 * (e.g. `/roles/new` before `/roles`, `/tax-rules/:id/edit` before `/tax-rules`).
 */
export interface Crumb {
  label: string
  /** Link target; omit for the current (last) page. */
  to?: string
}

interface BreadcrumbRule {
  /** Full pathname match test. */
  match: RegExp
  /** Ordered trail from section root to current page. */
  trail: Crumb[]
}

// Section roots always link back to /dashboard (the workspace home), matching the sidebar.
const S = {
  overview: { label: 'Overview', to: '/dashboard' },
  workforce: { label: 'Workforce', to: '/dashboard' },
  time: { label: 'Time & Attendance', to: '/dashboard' },
  payroll: { label: 'Payroll', to: '/payroll' },
  expenses: { label: 'Expenses', to: '/expenses' },
  communication: { label: 'Communication', to: '/dashboard' },
  self: { label: 'My Workspace', to: '/dashboard' },
  reports: { label: 'Reports', to: '/reports' },
  settings: { label: 'Settings', to: '/dashboard' },
} as const

const RULES: BreadcrumbRule[] = [
  // ---- Home ----
  { match: /^\/dashboard(\/|$)/, trail: [{ label: 'Dashboard' }] },

  // ---- Overview, Workforce, Time & Attendance, Communication ----
  { match: /^\/wallboard(\/|$)/, trail: [S.time, { label: 'WallBoard' }] },

  { match: /^\/companies\/new(\/|$)/, trail: [S.overview, { label: 'Companies', to: '/companies' }, { label: 'New company' }] },
  { match: /^\/companies\/setup-wizard\/imports(\/|$)/, trail: [S.overview, { label: 'Companies', to: '/companies' }, { label: 'Import history' }] },
  { match: /^\/companies\/setup-wizard(\/|$)/, trail: [S.overview, { label: 'Companies', to: '/companies' }, { label: 'Setup Wizard' }] },
  { match: /^\/companies\/[^/]+\/attendance\/time-corrections(\/|$)/, trail: [S.overview, { label: 'Companies', to: '/companies' }, { label: 'Company' }, { label: 'Time corrections' }] },
  { match: /^\/companies\/[^/]+\/attendance(\/|$)/, trail: [S.overview, { label: 'Companies', to: '/companies' }, { label: 'Company' }, { label: 'Attendance' }] },
  { match: /^\/companies\/[^/]+(\/|$)/, trail: [S.overview, { label: 'Companies', to: '/companies' }, { label: 'Company detail' }] },
  { match: /^\/companies(\/|$)/, trail: [S.overview, { label: 'Companies' }] },

  { match: /^\/attendance(\/|$)/, trail: [S.time, { label: 'Attendance' }] },
  { match: /^\/time-corrections(\/|$)/, trail: [S.time, { label: 'Time corrections' }] },
  { match: /^\/shifts(\/|$)/, trail: [S.time, { label: 'Shifts' }] },
  { match: /^\/leaves(\/|$)/, trail: [S.time, { label: 'Leave' }] },
  { match: /^\/penalties(\/|$)/, trail: [S.payroll, { label: 'Penalties' }] },
  { match: /^\/bonuses(\/|$)/, trail: [S.payroll, { label: 'Bonuses' }] },
  { match: /^\/security-ledger(\/|$)/, trail: [S.payroll, { label: 'Security deposit' }] },
  { match: /^\/documents(\/|$)/, trail: [S.workforce, { label: 'Documents' }] },
  { match: /^\/candidates(\/|$)/, trail: [S.workforce, { label: 'Candidates' }] },
  { match: /^\/hr(\/|$)/, trail: [S.workforce, { label: 'People & HR' }] },
  { match: /^\/members(\/|$)/, trail: [S.workforce, { label: 'Members' }] },
  { match: /^\/boards\/archived(\/|$)/, trail: [S.communication, { label: 'Boards', to: '/boards' }, { label: 'Archived boards' }] },
  { match: /^\/boards\/[^/]+(\/|$)/, trail: [S.communication, { label: 'Boards', to: '/boards' }, { label: 'Board' }] },
  { match: /^\/boards(\/|$)/, trail: [S.communication, { label: 'Boards' }] },
  { match: /^\/channels(\/|$)/, trail: [S.communication, { label: 'Channels' }] },

  { match: /^\/users\/new(\/|$)/, trail: [S.workforce, { label: 'Users', to: '/users' }, { label: 'New user' }] },
  { match: /^\/users(\/|$)/, trail: [S.workforce, { label: 'Users' }] },

  // ---- Payroll ----
  { match: /^\/payroll\/me\/slips\/[^/]+(\/|$)/, trail: [S.self, { label: 'My Payroll', to: '/payroll/me' }, { label: 'Slip detail' }] },
  { match: /^\/payroll\/me(\/|$)/, trail: [S.self, { label: 'My Payroll' }] },
  { match: /^\/payroll\/history\/[^/]+(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Payment history' }] },
  { match: /^\/payroll\/history(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Payment history' }] },
  { match: /^\/payroll\/forecast\/items\/[^/]+\/[^/]+(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Forecast' }, { label: 'Item detail' }] },
  { match: /^\/payroll\/forecast(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Forecast' }] },
  { match: /^\/payroll\/overview(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Overview' }] },
  { match: /^\/payroll\/batches\/new(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'New batch' }] },
  { match: /^\/payroll\/batches\/[^/]+\/items\/[^/]+(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Batch' }, { label: 'Item detail' }] },
  { match: /^\/payroll\/batches\/[^/]+(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Batch detail' }] },
  { match: /^\/companies\/[^/]+\/payroll\/runs\/[^/]+\/adjust(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Run' }, { label: 'Adjustment' }] },
  { match: /^\/companies\/[^/]+\/payroll\/runs\/[^/]+\/items\/[^/]+(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Run' }, { label: 'Item detail' }] },
  { match: /^\/companies\/[^/]+\/payroll\/runs\/[^/]+(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Run detail' }] },
  { match: /^\/companies\/[^/]+\/payroll\/runs(\/|$)/, trail: [S.payroll, { label: 'Payroll', to: '/payroll' }, { label: 'Runs' }] },
  { match: /^\/payroll(\/|$)/, trail: [S.payroll, { label: 'Payroll' }] },

  // ---- Payroll (tax rules) and Settings ----
  { match: /^\/settings\/permissions-matrix(\/|$)/, trail: [S.settings, { label: 'Permissions Matrix' }] },
  { match: /^\/roles\/new(\/|$)/, trail: [S.settings, { label: 'Roles', to: '/roles' }, { label: 'Create role' }] },
  { match: /^\/roles(\/|$)/, trail: [S.settings, { label: 'Roles' }] },
  { match: /^\/tax-rules\/new(\/|$)/, trail: [S.payroll, { label: 'Tax Rules', to: '/tax-rules' }, { label: 'New rule' }] },
  { match: /^\/tax-rules\/[^/]+\/edit(\/|$)/, trail: [S.payroll, { label: 'Tax Rules', to: '/tax-rules' }, { label: 'Edit rule' }] },
  { match: /^\/tax-rules\/[^/]+\/history(\/|$)/, trail: [S.payroll, { label: 'Tax Rules', to: '/tax-rules' }, { label: 'Version history' }] },
  { match: /^\/tax-rules\/[^/]+(\/|$)/, trail: [S.payroll, { label: 'Tax Rules', to: '/tax-rules' }, { label: 'Rule detail' }] },
  { match: /^\/tax-rules(\/|$)/, trail: [S.payroll, { label: 'Tax Rules' }] },
  { match: /^\/settings(\/|$)/, trail: [S.settings, { label: 'Settings' }] },

  // ---- My Workspace ----
  { match: /^\/my-shifts(\/|$)/, trail: [S.self, { label: 'My Shifts' }] },
  { match: /^\/my-attendance(\/|$)/, trail: [S.self, { label: 'My Attendance' }] },
  { match: /^\/my-time-corrections(\/|$)/, trail: [S.self, { label: 'My Time Corrections' }] },
  { match: /^\/my-leaves(\/|$)/, trail: [S.self, { label: 'My Leaves' }] },
  { match: /^\/my-bonuses(\/|$)/, trail: [S.self, { label: 'My Bonuses' }] },
  { match: /^\/my-expenses(\/|$)/, trail: [S.self, { label: 'My Expenses' }] },
  { match: /^\/my-deposits(\/|$)/, trail: [S.self, { label: 'My Security Deposit' }] },
  { match: /^\/my-documents(\/|$)/, trail: [S.self, { label: 'My Documents' }] },
  { match: /^\/my-policies\/[^/]+/, trail: [S.self, { label: 'My Policies', to: '/my-policies' }, { label: 'Policy' }] },
  { match: /^\/my-policies(\/|$)/, trail: [S.self, { label: 'My Policies' }] },
  { match: /^\/my-hr-cases\/new$/, trail: [S.self, { label: 'My HR Cases', to: '/my-hr-cases' }, { label: 'Raise a grievance' }] },
  { match: /^\/my-hr-cases\/[^/]+/, trail: [S.self, { label: 'My HR Cases', to: '/my-hr-cases' }, { label: 'Case' }] },
  { match: /^\/my-hr-cases(\/|$)/, trail: [S.self, { label: 'My HR Cases' }] },
  { match: /^\/my-letters\/[^/]+/, trail: [S.self, { label: 'My Letters', to: '/my-letters' }, { label: 'Letter' }] },
  { match: /^\/my-letters(\/|$)/, trail: [S.self, { label: 'My Letters' }] },

  // ---- Expenses, Reports, Communication, Settings ----
  { match: /^\/expense-analytics(\/|$)/, trail: [S.expenses, { label: 'Expense Analytics' }] },
  { match: /^\/expense-approvals(\/|$)/, trail: [S.expenses, { label: 'Expense Approvals' }] },
  { match: /^\/expenses(\/|$)/, trail: [S.expenses, { label: 'Expenses' }] },
  { match: /^\/audit-logs(\/|$)/, trail: [S.reports, { label: 'Audit logs' }] },
  { match: /^\/reports(\/|$)/, trail: [S.reports, { label: 'Reports' }] },
  { match: /^\/announcements(\/|$)/, trail: [S.communication, { label: 'Announcements' }] },
  { match: /^\/notifications(\/|$)/, trail: [{ label: 'Notifications' }] },
  { match: /^\/organization(\/|$)/, trail: [S.settings, { label: 'Organization' }] },
  { match: /^\/billing\/return(\/|$)/, trail: [S.settings, { label: 'Billing & plan', to: '/billing' }, { label: 'Checkout' }] },
  { match: /^\/billing(\/|$)/, trail: [S.settings, { label: 'Billing & plan' }] },
  { match: /^\/support\/[^/]+(\/|$)/, trail: [S.settings, { label: 'Help & support', to: '/support' }, { label: 'Ticket' }] },
  { match: /^\/support(\/|$)/, trail: [S.settings, { label: 'Help & support' }] },
]

/**
 * Resolve the breadcrumb trail for a pathname. Returns the trail (section root → current page)
 * for the first matching rule, or null for routes without a mapping (e.g. dashboard), so the
 * header shows nothing.
 */
export function getPageBreadcrumb(pathname: string): Crumb[] | null {
  for (const rule of RULES) {
    if (rule.match.test(pathname)) {
      // A page named like its menu ("Payroll › Payroll") shows the menu once.
      const [root, next] = rule.trail
      return next && root.label === next.label ? rule.trail.slice(1) : rule.trail
    }
  }
  return null
}
